// App.js
import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import theme from './theme/theme';
import Navbar from './components/Layout/Navbar';
import Sidebar from './components/Layout/Sidebar';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Organisations from './pages/Organisations/Organisations';
import OrganisationDetail from './pages/Organisations/OrganisationDetail';
import GroupDetail from './pages/Groups/GroupDetail';
import Settings from './pages/Settings/Settings';
import Logs from './pages/Logs/Logs';
import WikiWasteSearch from './pages/WikiWasteSearch/WikiWasteSearch';
import Administrators from './pages/Administrators/Administrators';
import GroupAdministrators from './pages/GroupAdministrators/GroupAdministrators';
import LiveAdverts from './pages/LiveAdverts/LiveAdverts';
import AdvertLibrary from './pages/AdvertLibrary/AdvertLibrary';
import Events from './pages/Events/Events';
import GroupLogs from './pages/GroupLogs/GroupLogs';
import FullPageLoader from './components/Common/FullPageLoader';

// Route tracker - saves current route when authenticated
const RouteTracker = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip first render to avoid overwriting saved route on initial load
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (isAuthenticated) {
      const currentPath = location.pathname;
      // Only save if it's a valid route (not login, not root)
      if (currentPath !== '/login' && currentPath !== '/' && currentPath !== '') {
        console.log('Saving route:', currentPath);
        localStorage.setItem('lastRoute', currentPath);
      }
    }
  }, [isAuthenticated, location.pathname]);

  return null;
};

// Main app content with route restoration
const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasRestored = useRef(false);

  // Restore route on initial load
  useEffect(() => {
    // Only run once when authentication is resolved and user is authenticated
    if (!loading && isAuthenticated && !hasRestored.current) {
      const currentPath = location.pathname;
      
      console.log('Current path:', currentPath);
      console.log('Last route from localStorage:', localStorage.getItem('lastRoute'));
      console.log('Redirect after login:', localStorage.getItem('redirectAfterLogin'));

      // If we're on root or login page, try to restore
      if (currentPath === '/' || currentPath === '/login') {
        // Priority 1: Check for redirect after login
        const redirectAfterLogin = localStorage.getItem('redirectAfterLogin');
        if (redirectAfterLogin && redirectAfterLogin !== '/login') {
          console.log('Restoring from redirectAfterLogin:', redirectAfterLogin);
          localStorage.removeItem('redirectAfterLogin');
          navigate(redirectAfterLogin, { replace: true });
          hasRestored.current = true;
          return;
        }
        
        // Priority 2: Use the last saved route
        const lastRoute = localStorage.getItem('lastRoute');
        if (lastRoute && lastRoute !== '/login' && lastRoute !== '/') {
          console.log('Restoring from lastRoute:', lastRoute);
          navigate(lastRoute, { replace: true });
          hasRestored.current = true;
          return;
        }
        
        // Priority 3: Default to dashboard
        console.log('No saved route, going to dashboard');
        navigate('/dashboard', { replace: true });
        hasRestored.current = true;
      } else {
        // User is already on a valid route, save it
        if (currentPath !== '/login' && currentPath !== '/') {
          localStorage.setItem('lastRoute', currentPath);
        }
        hasRestored.current = true;
      }
    }
  }, [isAuthenticated, loading, navigate, location.pathname]);

  if (loading) {
    return <FullPageLoader />;
  }

  // If not authenticated, show login routes
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Authenticated routes
  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Navbar />
      <Sidebar />
      <RouteTracker />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: '64px',
          ml: 0,
          width: '100%',
          height: 'calc(100vh - 64px)',
          backgroundColor: '#f5f7fa',
          overflow: 'auto'
        }}
      >
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/organisations" element={<Organisations />} />
          <Route path="/organisations/:id" element={<OrganisationDetail />} />
          <Route path="/groups/:id" element={<GroupDetail />} />
          
          <Route path="/settings" element={<Settings />} />
          <Route path="/administrators" element={<Administrators />} />
          
          <Route path="/logs" element={<Logs />} />
          <Route path="/wikiwaste-search" element={<WikiWasteSearch />} />
          
          <Route path="/group-administrators/:groupId" element={<GroupAdministrators />} />
          <Route path="/live-adverts/:groupId" element={<LiveAdverts />} />
          <Route path="/advert-library/:groupId" element={<AdvertLibrary />} />
          <Route path="/events/:groupId" element={<Events />} />
          <Route path="/group-logs/:groupId" element={<GroupLogs />} />
          
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Box>
    </Box>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;